import { Button, Form, Input, Segmented, Select, Tooltip } from "antd";
import { Eye, LifeBuoy, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useGetEmployeesQuery } from "../../features/employee/employeeApi";
import {
  useCreateSupportMutation,
  useGetMySupportListQuery,
} from "../../features/supportTicket/supportTicketApi";
import useError from "../../hooks/useError";
import useModal from "../../hooks/useModal";
import { formatUTCDate } from "../../lib/time";
import { toast } from "../../lib/toast";
import { GetSupportParamsType } from "../../types";
import { capitalize } from "../../utils/string";
import { selectSearchOption } from "../../utils/utils";
import EmployeeFileUpload from "../Employees/EmployeeFileUpload";
import CustomEmpty from "../ui/CustomEmpty";
import CustomModal from "../ui/CustomModal";
import Loader from "../ui/Loader";
import RequestDetailsModal from "./RequestDetailsModal";

type Props = {
  memberId?: number;
};

type ModalType = "CREATE" | "VIEW";

const statusClass = {
  open: "status-danger",
  closed: "status-success",
};

export default function UserSupportTickets({ memberId }: Props) {
  const [status, setStatus] = useState<GetSupportParamsType["status"]>("");
  const { open, handleOpen, handleClose } = useModal<string, ModalType>();
  const { data, isLoading, isError, error } = useGetMySupportListQuery({
    params: { status, skip: 0, limit: 0 },
  });

  useError(isError, error, "Get support tickets fail");

  const tickets = data?.data?.supports || [];

  return (
    <div className="min-h-[420px]">
      <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-extra-light">
        <div className="flex items-center gap-2">
          <LifeBuoy size={20} className="text-primary" />
          <h2 className="text-header text-lg font-semibold">Support Tickets</h2>
        </div>
        <Button
          type="primary"
          icon={<Plus size={16} />}
          onClick={() => handleOpen({ type: "CREATE" })}
          disabled={!memberId}
        >
          New Ticket
        </Button>
      </div>

      <div className="py-4">
        <Segmented
          value={status}
          options={[
            { label: "All", value: "" },
            { label: "Open", value: "open" },
            { label: "Closed", value: "closed" },
            { label: "Assigned", value: "assigned" },
          ]}
          onChange={(value) =>
            setStatus(value as GetSupportParamsType["status"])
          }
        />
      </div>

      {isLoading ? (
        <Loader />
      ) : tickets.length ? (
        <div className="divide-y divide-extra-light border-y border-extra-light">
          {tickets.map((ticket) => (
            <div
              key={ticket.id}
              className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 items-center py-4"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 className="font-semibold text-body truncate">
                    {ticket.title || "Untitled ticket"}
                  </h3>
                  <span
                    className={
                      statusClass[ticket.status as keyof typeof statusClass] ||
                      ""
                    }
                  >
                    {capitalize(ticket.status)}
                  </span>
                </div>
                <p className="text-sm text-display-body line-clamp-2">
                  {ticket.long_description}
                </p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted">
                  <span>Ticket #{ticket.id}</span>
                  <span>{formatUTCDate(ticket.created_at)}</span>
                  <span>
                    Assigned to: {ticket.assign_to_info?.name || "Pending"}
                  </span>
                </div>
              </div>
              <Tooltip title="View ticket">
                <Button
                  type="text"
                  className="action-button"
                  icon={<Eye size={17} />}
                  onClick={() =>
                    handleOpen({ type: "VIEW", data: String(ticket.id) })
                  }
                />
              </Tooltip>
            </div>
          ))}
        </div>
      ) : (
        <CustomEmpty description="No support tickets found" />
      )}

      {open.type === "CREATE" && memberId ? (
        <CreateSupportTicketModal
          open={open.open}
          memberId={memberId}
          handleClose={handleClose}
        />
      ) : null}

      {open.type === "VIEW" ? (
        <RequestDetailsModal
          open={open.open}
          data={open.data}
          allowFeedback
          currentMemberId={memberId}
          handleClose={handleClose}
        />
      ) : null}
    </div>
  );
}

function CreateSupportTicketModal({
  memberId,
  handleClose,
  ...rest
}: {
  memberId: number;
  open: boolean;
  handleClose: () => void;
}) {
  const [form] = Form.useForm();
  const [attachment, setAttachment] = useState<string | null>(null);
  const {
    data: employees,
    isLoading: isEmployeesLoading,
    isError: isEmployeesError,
    error: employeesError,
  } = useGetEmployeesQuery({
    params: { skip: 0, limit: 0, status: "active" },
  });
  const [
    createSupport,
    { isLoading, isError, error, isSuccess, reset },
  ] = useCreateSupportMutation();

  useError(isError, error, "Create support ticket fail");
  useError(isEmployeesError, employeesError, "Get assignee list fail");

  const assigneeOptions = employees?.data?.employees
    ?.filter((employee) => employee.member_id !== memberId)
    .map((employee) => ({
      label: `${employee.member?.profile?.name || "Unnamed employee"}${
        employee.role?.name ? ` - ${employee.role.name}` : ""
      }`,
      value: employee.member_id,
    }));

  const closeModal = () => {
    form.resetFields();
    setAttachment(null);
    reset();
    handleClose();
  };

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Support ticket created successfully");
      closeModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <CustomModal
      title="Create Support Ticket"
      {...rest}
      handleClose={closeModal}
      width={560}
      footer={null}
      closable={!isLoading}
    >
      <Form
        form={form}
        layout="vertical"
        className="custom-form"
        onFinish={(values) =>
          createSupport({
            member_id: memberId,
            assign_to_member_id: values.assign_to_member_id,
            title: values.title.trim(),
            long_description: values.long_description.trim(),
            attachment,
            status: "open",
          })
        }
      >
        <Form.Item
          label="Title"
          name="title"
          rules={[
            { required: true, message: "Please enter a ticket title" },
            { max: 150, message: "Title cannot exceed 150 characters" },
          ]}
        >
          <Input placeholder="Briefly describe the issue" size="large" />
        </Form.Item>

        <Form.Item
          label="Assign to"
          name="assign_to_member_id"
          rules={[{ required: true, message: "Please select an assignee" }]}
        >
          <Select
            size="large"
            showSearch
            loading={isEmployeesLoading}
            disabled={isEmployeesLoading}
            filterOption={selectSearchOption}
            options={assigneeOptions}
            placeholder="Select an employee"
          />
        </Form.Item>

        <Form.Item
          label="What support do you need?"
          name="long_description"
          rules={[
            { required: true, message: "Please describe the support needed" },
            {
              max: 2000,
              message: "Description cannot exceed 2000 characters",
            },
          ]}
        >
          <Input.TextArea
            placeholder="Include the steps, expected result, and what happened"
            autoSize={{ minRows: 5, maxRows: 9 }}
            showCount
            maxLength={2000}
          />
        </Form.Item>

        <Form.Item label="Attachment" name="attachment">
          <EmployeeFileUpload
            form={form}
            name="attachment"
            accept=".png,.jpg,.jpeg,.pdf"
            listFile={false}
            fileSize={10}
            onChange={(url) => {
              setAttachment(url);
              form.setFieldValue("attachment", url);
            }}
          />
        </Form.Item>

        <div className="flex justify-end gap-3 mt-6">
          <Button onClick={closeModal} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            Submit Ticket
          </Button>
        </div>
      </Form>
    </CustomModal>
  );
}
